import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: benefit-plan-tabs subdirectory requires four levels up to reach convex/_generated/
import { Card } from '@/components/ui/card';
import { PlanInfoCard } from '@/components/insurance/benefit-plan-tabs/PlanInfoCard';
import { Info, Receipt } from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  IBenefitPlan,
  IBenefitSection,
  IBenefitItem,
  IPlanSummaryField,
  IPlanInfoRow,
} from '@/@types/claims';

interface IPlanSummaryTabProps {
  // LOCAL_TYPE_JUSTIFIED: component-specific props combining plan with enriched sections
  plan: IBenefitPlan & {
    sections: Array<IBenefitSection & { items: IBenefitItem[] }>;
  };
}

function getField(fields: IPlanSummaryField[], key: string): string {
  return fields.find(f => f.key === key)?.value ?? '';
}

function formatCurrency(value: number | undefined): string {
  if (value === undefined || value === null) return '€ 0';
  return `€ ${value.toLocaleString()}`;
}

export function PlanSummaryTab({ plan }: IPlanSummaryTabProps) {
  const updatePlan = useMutation(api.insurance.benefitPlans.update);
  const updateTabData = useMutation(api.insurance.benefitPlans.updateTabData);
  const fields = plan.planSummaryFields ?? [];

  const isZeroDeductible = plan.annualDeductible === 0;
  const isZeroMemberPay = plan.coinsuranceYouPay === 0;

  const stats = [
    {
      label: 'MAX ANNUAL BENEFIT',
      value: formatCurrency(plan.maxAnnualBenefit),
      subtitle: 'Per person, per year',
      highlight: false,
    },
    {
      label: 'ANNUAL DEDUCTIBLE',
      value: formatCurrency(plan.annualDeductible),
      subtitle: isZeroDeductible ? 'No deductible applies' : 'Per person, per year',
      highlight: isZeroDeductible,
    },
    {
      label: 'PLAN PAYS',
      value: `${plan.coinsurancePlanPays ?? 0}%`,
      subtitle: 'Of allowable charges',
      highlight: false,
    },
    {
      label: 'MEMBER PAYS',
      value: `${plan.coinsuranceYouPay ?? 0}%`,
      subtitle: 'Coinsurance/copayment',
      highlight: isZeroMemberPay,
    },
  ];

  const overviewRows: IPlanInfoRow[] = [
    { label: 'Plan Name', value: plan.name, saveKey: 'name', source: 'plan', editable: true },
    {
      label: 'Group Number',
      value: getField(fields, 'groupNumber'),
      saveKey: 'groupNumber',
      source: 'extra',
    },
    {
      label: 'Currency',
      value: plan.currency,
      saveKey: 'currency',
      source: 'plan',
      badge: plan.currency === 'EUR' ? '€' : undefined,
    },
    {
      label: 'Area of Coverage',
      value: plan.areaOfCoverage ?? '',
      saveKey: 'areaOfCoverage',
      source: 'plan',
    },
    {
      label: 'Emergency Outside Area',
      value: getField(fields, 'emergencyOutsideArea'),
      saveKey: 'emergencyOutsideArea',
      source: 'extra',
    },
    {
      label: 'Other Member Responsibility',
      value: getField(fields, 'otherMemberResponsibility'),
      saveKey: 'otherMemberResponsibility',
      source: 'extra',
    },
    {
      label: 'General Claims Basis',
      value: getField(fields, 'generalClaimsBasis'),
      saveKey: 'generalClaimsBasis',
      source: 'extra',
    },
  ];

  const claimsRows: IPlanInfoRow[] = [
    {
      label: 'Claims Basis',
      value: getField(fields, 'claimsBasis'),
      saveKey: 'claimsBasis',
      source: 'extra',
    },
    {
      label: 'Submission Timing',
      value: getField(fields, 'submissionTiming'),
      saveKey: 'submissionTiming',
      source: 'extra',
    },
    {
      label: 'Reimbursement SLA',
      value: getField(fields, 'reimbursementSla'),
      saveKey: 'reimbursementSla',
      source: 'extra',
    },
    {
      label: 'Pre-Approval Penalty',
      value: getField(fields, 'preApprovalPenalty'),
      saveKey: 'preApprovalPenalty',
      source: 'extra',
      color: 'danger',
    },
    {
      label: 'OON Responsibility',
      value: getField(fields, 'oonResponsibility'),
      saveKey: 'oonResponsibility',
      source: 'extra',
      color: 'warning',
    },
  ];

  async function handleSave(key: string, value: string, source: 'plan' | 'extra') {
    if (source === 'plan') {
      const numericKeys = [
        'maxAnnualBenefit',
        'annualDeductible',
        'coinsurancePlanPays',
        'coinsuranceYouPay',
      ];
      const patch: Record<string, string | number> = {};
      if (numericKeys.includes(key)) {
        const parsed = parseFloat(value);
        if (!isNaN(parsed)) patch[key] = parsed;
      } else {
        patch[key] = value;
      }
      await updatePlan({ planId: plan._id, ...patch } as Parameters<typeof updatePlan>[0]); // AS_JUSTIFIED: dynamic key spread from validated plan fields
    } else {
      const current: IPlanSummaryField[] = plan.planSummaryFields ?? [];
      const existing = current.find(f => f.key === key);
      const updated = existing
        ? current.map(f => (f.key === key ? { ...f, value } : f))
        : [...current, { key, label: key, value }];
      await updateTabData({ planId: plan._id, field: 'planSummaryFields', data: updated });
    }
  }

  return (
    <div className="space-y-6 py-6 px-8">
      {/* Stat Cards */}
      <div className="grid grid-cols-4 gap-4">
        {stats.map(stat => (
          <Card key={stat.label} className="rounded-xl border px-5 py-4 space-y-1.5">
            <p className="text-[10px] font-semibold tracking-[0.5px] uppercase text-muted-foreground/70">
              {stat.label}
            </p>
            <p
              className={cn(
                'text-[22px] font-bold leading-tight',
                stat.highlight ? 'text-lime-500' : 'text-foreground'
              )}
            >
              {stat.value}
            </p>
            <p className="text-xs text-muted-foreground">{stat.subtitle}</p>
          </Card>
        ))}
      </div>

      {/* Two-column info cards */}
      <div className="grid grid-cols-2 gap-5">
        <PlanInfoCard
          icon={<Info className="h-4 w-4 text-lime-500" />}
          title="Plan Overview"
          rows={overviewRows}
          onSave={handleSave}
        />
        <PlanInfoCard
          icon={<Receipt className="h-4 w-4 text-lime-500" />}
          title="Claims & Billing"
          rows={claimsRows}
          onSave={handleSave}
        />
      </div>
    </div>
  );
}
